import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import DynamicButton from '../components/dynamic/DynamicButton';
import { communityAPI } from '../services/api';
import { CommunityPlatform } from '../types';
import { 
  FaBriefcase, 
  FaGithub, 
  FaLinkedin, 
  FaStar,
  FaCheckCircle,
  FaExternalLinkAlt
} from 'react-icons/fa';

const iconMap: { [key: string]: any } = {
  FaBriefcase,
  FaGithub,
  FaLinkedin,
};

const Community: React.FC = () => {
  const [platforms, setPlatforms] = useState<CommunityPlatform[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadPlatforms = async () => {
      try {
        const response = await communityAPI.getAll();
        if (response && response.data) {
          setPlatforms(response.data);
        }
      } catch (error: any) {
        console.error('Error loading community platforms:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPlatforms();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading community platforms...</div>
      </div>
    );
  }

  if (platforms.length === 0) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="text-xl text-gray-600 dark:text-gray-400 mb-4">No community platforms found</div>
          <div className="text-sm text-gray-500 dark:text-gray-500">Please check the server logs for errors</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Community & Platforms"
        subtitle="Connect with me on various platforms and explore my work"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {platforms.map((platform) => {
            const IconComponent = iconMap[platform.icon] || FaBriefcase;
            return (
              <DynamicCard key={platform.id} className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {platform.name}
                      </h3>
                      {platform.verified && (
                        <FaCheckCircle className="text-green-500 dark:text-green-400" />
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-3">
                      @{platform.username}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      {platform.description}
                    </p>
                    {platform.rating && (
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.floor(platform.rating!)
                                  ? 'text-yellow-400'
                                  : 'text-gray-300 dark:text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {platform.rating}
                        </span>
                        {platform.reviews && platform.reviews > 0 && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            ({platform.reviews} reviews)
                          </span>
                        )}
                      </div>
                    )}
                    <DynamicButton
                      variant="primary"
                      onClick={() => window.open(platform.url, '_blank')}
                      className="w-full"
                    >
                      Visit Profile
                      <FaExternalLinkAlt className="ml-2 inline" />
                    </DynamicButton>
                  </div>
                </div>
              </DynamicCard>
            );
          })}
        </div>
      </DynamicSection>
    </div>
  );
};

export default Community;
